/**
 * Support access (098) — the owner opens his shop to MartePOS support for a
 * while, with a one-time code, and sees everything support changed.
 *
 * The database decides who gets in and what they may do (support_grants, a
 * temporary membership auth_org prefers while it lives, SUPPORT_CANNOT on
 * money). These helpers only mirror it for the screen: which membership is in
 * force, how long is left, and how a logged change reads in Georgian.
 */

import { t, type Key } from './i18n'

export type SupportDuration = 60 | 240 | 1440

export const SUPPORT_DURATIONS: { minutes: SupportDuration; label: Key }[] = [
  { minutes: 60, label: 'support.dur.60' },
  { minutes: 240, label: 'support.dur.240' },
  { minutes: 1440, label: 'support.dur.1440' },
]

/** What the admin's own app knows about the session it is in. */
export type SupportSession = { canWrite: boolean; endsAt: string; grantId: string }

/** What support_status() answers. */
export type SupportStatus =
  | { mode: 'none' }
  | { mode: 'pending'; grant_id: string; can_write: boolean; minutes: number; valid_until: string; sent_at: string | null }
  | { mode: 'active'; grant_id: string; can_write: boolean; started_at: string; ends_at: string }
  | { mode: 'support'; grant_id: string; org_name: string; billing_no: number; can_write: boolean; ends_at: string }

export type MembershipRow = {
  org_id: string
  role: string
  created_at: string | null
  expires_at: string | null
  support_grant_id: string | null
}

/**
 * The membership in force — the same rule as auth_org(): an unexpired
 * temporary (support) row first, an expired one never, then the oldest.
 */
export function pickMembership<T extends MembershipRow>(rows: T[], now = Date.now()): T | null {
  const live = rows.filter((r) => !r.expires_at || new Date(r.expires_at).getTime() > now)
  live.sort((a, b) =>
    (a.expires_at ? 0 : 1) - (b.expires_at ? 0 : 1)
    || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
    || a.org_id.localeCompare(b.org_id))
  return live[0] ?? null
}

/** Letters and digits only, upper case — what the database hashes. */
export const normalizeCode = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '')

/** K7Q2-9MXD — how the code is shown and read over the phone. */
export function formatCode(s: string): string {
  const n = normalizeCode(s).slice(0, 8)
  return n.length > 4 ? `${n.slice(0, 4)}-${n.slice(4)}` : n
}

/** „57 წთ", „3 სთ 12 წთ", „0 წთ". */
export function leftLabel(until: string, now = Date.now()): string {
  const mins = Math.max(0, Math.ceil((new Date(until).getTime() - now) / 60_000))
  if (mins < 60) return t('support.left.m', { m: mins })
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? t('support.left.hm', { h, m }) : t('support.left.h', { h })
}

export const SUPPORT_ERRORS: Record<string, Key> = {
  INVALID_CODE: 'support.err.INVALID_CODE',
  TOO_MANY_ATTEMPTS: 'support.err.TOO_MANY_ATTEMPTS',
  OWN_SHOP: 'support.err.OWN_SHOP',
  GRANT_NOT_OPEN: 'support.err.GRANT_NOT_OPEN',
  NOT_SENT: 'support.err.NOT_SENT',
  SUPPORT_CANNOT: 'support.err.SUPPORT_CANNOT',
}

export const supportErrorText = (codeOrMessage: string | null | undefined) => {
  const m = codeOrMessage ?? ''
  const key = Object.keys(SUPPORT_ERRORS).find((k) => m.includes(k))
  return t(key ? SUPPORT_ERRORS[key] : 'support.err.default')
}

// ---------------------------------------------------------------- the change log

export type SupportChange = {
  id: number
  grant_id: string
  table_name: string
  op: 'INSERT' | 'UPDATE' | 'DELETE'
  row_id: string | null
  label: string | null
  diff: Record<string, [string | null, string | null]> | null
  created_at: string
}

const TABLES: Record<string, Key> = {
  products: 'support.tbl.products',
  categories: 'support.tbl.categories',
  product_addons: 'support.tbl.product_addons',
  product_ingredients: 'support.tbl.product_ingredients',
  product_conversions: 'support.tbl.product_conversions',
  product_breakdowns: 'support.tbl.product_breakdowns',
  product_breakdown_parts: 'support.tbl.product_breakdown_parts',
  suppliers: 'support.tbl.suppliers',
  supplier_product_codes: 'support.tbl.supplier_product_codes',
  purchases: 'support.tbl.purchases',
  purchase_items: 'support.tbl.purchase_items',
  promotions: 'support.tbl.promotions',
  promotion_items: 'support.tbl.promotion_items',
  customers: 'support.tbl.customers',
  settings: 'support.tbl.settings',
  branches: 'support.tbl.branches',
  cashiers: 'support.tbl.cashiers',
  stocktakes: 'support.tbl.stocktakes',
  stock_transfers: 'support.tbl.stock_transfers',
}

const OPS: Record<SupportChange['op'], Key> = { INSERT: 'support.op.INSERT', UPDATE: 'support.op.UPDATE', DELETE: 'support.op.DELETE' }

const FIELDS: Record<string, Key> = {
  name: 'support.fld.name', sale_price: 'support.fld.sale_price', purchase_price: 'support.fld.purchase_price', wholesale_price: 'support.fld.wholesale_price',
  quantity: 'support.fld.quantity', barcode: 'support.fld.barcode', category_id: 'support.fld.category_id', unit: 'support.fld.unit', photo_url: 'support.fld.photo_url',
  plu_code: 'support.fld.plu_code', expiry_date: 'support.fld.expiry_date', value: 'support.fld.value', phone: 'support.fld.phone', note: 'support.fld.note',
  active: 'support.fld.active', tin: 'support.fld.tin', legal_name: 'support.fld.legal_name', legal_address: 'support.fld.legal_address', parent_id: 'support.fld.parent_id',
  price: 'support.fld.price', promo_price: 'support.fld.promo_price', pin: 'support.fld.pin', branch_id: 'support.fld.branch_id', is_default: 'support.fld.is_default',
}

/** The settings keys an owner would recognise; the rest stay as they are. */
const SETTINGS: Record<string, Key> = {
  companyName: 'support.set.companyName', companyId: 'support.set.companyId', address: 'support.set.address', phone: 'support.set.phone',
  bankDetails: 'support.set.bankDetails', taxStatus: 'support.set.taxStatus', loyaltyEnabled: 'support.set.loyaltyEnabled',
  loyaltyPct: 'support.set.loyaltyPct', scaleEnabled: 'support.set.scaleEnabled', shareCatalog: 'support.set.shareCatalog',
  cashierStocktake: 'support.set.cashierStocktake', cameraUrlTemplate: 'support.set.cameraUrlTemplate', fiscalKasaEnabled: 'support.set.fiscalKasaEnabled', fiscalDevice: 'support.set.fiscalDevice',
  receiptFooter: 'support.set.receiptFooter',
}

/** A logged row, as the owner reads it: „პროდუქტი „კაბელი" შეიცვალა — ფასი: 80.00 → 81.00". */
export function describeChange(c: SupportChange): { title: string; fields: { label: string; from: string; to: string }[] } {
  const what = TABLES[c.table_name] ? t(TABLES[c.table_name]) : c.table_name
  const setting = c.table_name === 'settings' && c.label ? SETTINGS[c.label] : undefined
  const label = setting ? t(setting) : c.label
  const op = OPS[c.op] ? t(OPS[c.op]) : c.op
  // Georgian says „პროდუქტი „კაბელი" შეიცვალა"; Russian puts the verb first
  // („Изменено: товар «кабель»") — its verb would agree with the noun's gender.
  const title = t('support.chg.title', { what, op, name: label ? t('support.chg.name', { name: label }) : '' })
  const fields = Object.entries(c.diff ?? {}).map(([k, [from, to]]) => ({
    label: FIELDS[k] ? t(FIELDS[k]) : k,
    from: from ?? '—',
    to: to ?? '—',
  }))
  return { title, fields }
}
